import React, { useMemo, useRef, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import { withRouter } from 'react-router-dom'

import CasesService from '../../../services/CasesService'

import ReportPageTemplate from './ReportPageTemplate'

export default withRouter(({ caseNumber, user, settings, showError, history }) => {
  const caseReportPromise = useMemo(() => CasesService.getInProgressReport(caseNumber), [caseNumber])
  const filesPromise = useMemo(() => CasesService.getFiles(caseNumber), [caseNumber])
  const [showSubmitReportConfirmation, setShowSubmitReportConfirmation] = useState(false)
  const [showReportSubmitting, setReportSubmitting] = useState(false)
  const [submissionNote, setSubmissionNote] = useState('')
  const reportModelRef = useRef()

  const handleSubmitReport = () => {
    setSubmissionNote('') // Clear note when opening
    setShowSubmitReportConfirmation(true)
  }

  const handleCloseSubmitReportConfirmationModal = () => {
    setShowSubmitReportConfirmation(false)
  }

  const handleSubmitReportConfirmed = () => {
    setShowSubmitReportConfirmation(false)

    setReportSubmitting(true)
    CasesService.submitReport(caseNumber, submissionNote)
      .onSuccess(() => {
        history.replace(`/cases/${caseNumber}/reports`)
      })
      .onBadRequest(err => {
        showError(err)
      })
      .onStatus(401, () => showError('Authentication failed. Please log in again.'))
      .onStatus(403, () => showError('You do not have permission to submit this report.'))
      .onStatus(404, () => showError('Report not found. It may have been deleted or moved.'))
      .onStatus(500, () => showError('Server error occurred. Please try again later.'))
      .catch(_err => {
        showError(
          'An unexpected error occurred while trying to submit a report. Please check your network connection and try again.',
        )
      })
      .then(() => {
        setReportSubmitting(false)
      })
  }

  const SubmitForReview = ({ reportModel }) => {
    reportModelRef.current = reportModel

    if (!showReportSubmitting) {
      return (
        <button id="submit-for-review-btn" className="btn btn-primary" onClick={handleSubmitReport}>
          Submit for Review
        </button>
      )
    } else {
      return (
        <>
          <button className="btn btn-primary" disabled>
            <Spinner animation="border" size="sm" className="text-white" />
            &nbsp; Submitting...
          </button>
        </>
      )
    }
  }

  return (
    <>
      <ReportPageTemplate
        caseNumber={caseNumber}
        isInProgress={true}
        user={user}
        caseReportPromise={caseReportPromise}
        filesPromise={filesPromise}
        settings={settings}
        actionsComponent={SubmitForReview}
        printableReportPath={`/cases/${caseNumber}/reports/in-progress/print`}
      />

      {showSubmitReportConfirmation &&
        (reportModelRef.current?.invalid ? (
          <Modal show={true} onHide={handleCloseSubmitReportConfirmationModal}>
            <Modal.Body>
              <h1 className="h6">Cannot submit for review until the follow issues are resolved:</h1>

              <div className="mb-4">
                {reportModelRef.current.invalidReasons.map((reason, i) => (
                  <div key={i} className="text-danger">
                    {reason}
                  </div>
                ))}
              </div>

              <div className="text-right">
                <button
                  type="button"
                  className="btn btn-secondary mr-1"
                  onClick={handleCloseSubmitReportConfirmationModal}
                >
                  Cancel
                </button>
              </div>
            </Modal.Body>
          </Modal>
        ) : (
          <Modal show={true} onHide={handleCloseSubmitReportConfirmationModal}>
            <Modal.Body>
              <h1 className="h6">Ready to Submit for Review?</h1>
              <p>Once the report has been submitted, the action cannot be undone.</p>

              <div className="form-group">
                <label htmlFor="submissionNote" className="form-label">
                  Submission Note (Optional)
                </label>
                <textarea
                  id="submissionNote"
                  className="form-control"
                  rows={3}
                  placeholder="Enter a note for the reviewer..."
                  value={submissionNote}
                  onChange={e => setSubmissionNote(e.target.value)}
                />
              </div>

              <div className="text-right">
                <button
                  type="button"
                  className="btn btn-secondary mr-1"
                  onClick={handleCloseSubmitReportConfirmationModal}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmitReportConfirmed}>
                  Confirm
                </button>
              </div>
            </Modal.Body>
          </Modal>
        ))}
    </>
  )
})
